import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Save, ArrowLeft, Plus, X, Image as ImageIcon } from 'lucide-react';

const UpdateProduct = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [categories, setCategories] = useState([]);

    // Form State
    const [name, setName] = useState('');
    const [catId, setCatId] = useState('');
    const [keyFeat, setKeyFeat] = useState('');
    const [subFeatures, setSubFeatures] = useState([]);
    const [mainFeatures, setMainFeatures] = useState([]);
    
    // Image States
    const [img1, setImg1] = useState(null);
    const [img2, setImg2] = useState(null);
    const [preview1, setPreview1] = useState('');
    const [preview2, setPreview2] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = localStorage.getItem('adminToken');
                const headers = { authorization: `Bearer ${token}` };
                
                // Fetch categories and product details in parallel
                const [catRes, prodRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/viewcategories`, { headers }),
                    axios.get(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/viewproduct/${id}`, { headers })
                ]);

                setCategories(catRes.data.categories);
                const p = prodRes.data.product;
                setName(p.productName);
                setCatId(p.categoryId._id || p.categoryId);
                setKeyFeat(p.keyFeatures);
                setSubFeatures(p.subFeatures);
                setMainFeatures(p.mainFeatures);
                setPreview1(p.productImage1);
                setPreview2(p.productImage2);
            } catch (err) {
                alert("Error fetching product details");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id]);

    const handleFeatureChange = (setter, list, index, field, value) => {
        const updated = [...list];
        updated[index][field] = value;
        setter(updated);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setUpdating(true);
        const formData = new FormData();
        formData.append('productName', name);
        formData.append('categoryId', catId);
        formData.append('keyFeatures', keyFeat);
        if (img1) formData.append('productImage1', img1);
        if (img2) formData.append('productImage2', img2);
        formData.append('subFeatures', JSON.stringify(subFeatures));
        formData.append('mainFeatures', JSON.stringify(mainFeatures));

        try {
            const token = localStorage.getItem('adminToken');
            await axios.put(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/updateproduct/${id}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data', authorization: `Bearer ${token}` }
            });
            alert("Updated Successfully!");
            navigate('/admin/products');
        } catch (err) {
            alert(err.message);
        } finally {
            setUpdating(false);
        }
    };

    if (loading) return <div className="h-screen flex items-center justify-center"><Loader2 className="animate-spin text-orange-500" /></div>;

    return (
        <div className="p-8 max-w-6xl mx-auto space-y-8">
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[10px] font-black uppercase text-slate-400 hover:text-orange-500">
                <ArrowLeft size={14} /> Back to Fleet
            </button>

            <h1 className="text-3xl font-black uppercase italic">Edit <span className="text-orange-500">Product</span></h1>

            <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                {/* LEFT COLUMN: BASIC INFO & IMAGES */}
                <div className="space-y-6">
                    <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase">Model Name</label>
                                <input type="text" value={name} onChange={(e)=>setName(e.target.value)} className="w-full p-3 bg-slate-50 rounded-xl font-bold" />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase">Category</label>
                                <select value={catId} onChange={(e)=>setCatId(e.target.value)} className="w-full p-3 bg-slate-50 rounded-xl font-bold">
                                    {categories.map(c => <option key={c._id} value={c._id}>{c.categoryName}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="space-y-1">
                            <label className="text-[10px] font-black text-slate-400 uppercase">Key Innovations</label>
                            <textarea value={keyFeat} onChange={(e)=>setKeyFeat(e.target.value)} className="w-full p-3 bg-slate-50 rounded-xl h-24" />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <ImageSlot label="Primary Visual" preview={preview1} onChange={(file) => {setImg1(file); setPreview1(URL.createObjectURL(file))}} />
                        <ImageSlot label="Secondary Visual" preview={preview2} onChange={(file) => {setImg2(file); setPreview2(URL.createObjectURL(file))}} />
                    </div>
                </div>

                {/* RIGHT COLUMN: ARRAYS */}
                <div className="space-y-6">
                    <FeatureEditor title="Technical Specifications" list={subFeatures} setter={setSubFeatures} handler={handleFeatureChange} />
                    <FeatureEditor title="Main Systems & Features" list={mainFeatures} setter={setMainFeatures} handler={handleFeatureChange} />

                    <button type="submit" disabled={updating} className="w-full py-5 bg-orange-500 text-white font-black uppercase tracking-widest rounded-2xl shadow-xl shadow-orange-500/20 flex justify-center items-center gap-2 hover:bg-slate-900 transition-all">
                        {updating ? <Loader2 className="animate-spin" /> : <><Save size={18}/> Update Machine Details</>}
                    </button>
                </div>
            </form>
        </div>
    );
};

const ImageSlot = ({ label, preview, onChange }) => (
    <div className="bg-white p-4 rounded-3xl border border-slate-100 shadow-sm space-y-3">
        <label className="text-[10px] font-black text-slate-400 uppercase">{label}</label>
        <div className="relative aspect-square bg-slate-50 rounded-2xl overflow-hidden group">
            <img src={preview} className="w-full h-full object-cover" alt="preview" />
            <label className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer text-white text-[10px] font-bold uppercase gap-2">
                <ImageIcon size={16}/> Swap Image
                <input type="file" className="hidden" onChange={(e) => onChange(e.target.files[0])} />
            </label>
        </div>
    </div>
);

const FeatureEditor = ({ title, list, setter, handler }) => (
    <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-4">
        <div className="flex justify-between items-center">
            <h3 className="text-[10px] font-black uppercase text-slate-400">{title}</h3>
            <button type="button" onClick={() => setter([...list, { title: '', description: '' }])} className="p-1 bg-slate-900 text-white rounded-md"><Plus size={14}/></button>
        </div>
        {list.map((item, idx) => (
            <div key={idx} className="flex gap-2">
                <input value={item.title} onChange={(e)=>handler(setter, list, idx, 'title', e.target.value)} className="w-1/3 p-2 text-xs bg-slate-50 rounded-lg" placeholder="Title" />
                <input value={item.description} onChange={(e)=>handler(setter, list, idx, 'description', e.target.value)} className="w-2/3 p-2 text-xs bg-slate-50 rounded-lg" placeholder="Description" />
                <button type="button" onClick={() => setter(list.filter((_, i) => i !== idx))}><X size={14} className="text-red-400"/></button>
            </div>
        ))}
    </div>
);

export default UpdateProduct;